// Shared schema classification used by writers, migration and security tests.
// Mixed maps are private in their entirety: rules do not redact map fields.
export const PRIVATE_FINANCIAL_FIELDS = [
  'payableHours', 'payrollCalculationVersion', 'payrollCalculatedAt', 'payrollProvenance', 'payrollReview',
  'payRate', 'workerRate', 'workerPayRate', 'hourlyPayRate', 'hourlyRate',
  'salary', 'salaryRate', 'basePay', 'overtimePayRate', 'rates', 'facilityRates',
  'grossPay', 'netPay', 'grossEarnings', 'workerPayAmount', 'payAmount', 'totalPay',
  'regularPay', 'overtimePay', 'payrollAmount', 'payrollCost', 'payroll',
  'compensation', 'compensationData', 'payrollData', 'payrollSummary', 'financialSummary',
  'margin', 'marginPercent', 'grossMargin', 'netMargin', 'profit',
  'projectedPayroll', 'projectedMargin', 'averagePayRate', 'avgPayRate',
  'payrollDeductions', 'payrollAdvances', 'payrollReimbursements', 'payrollPaidAt',
  'payrollStatus', 'payrollHours', 'payrollId', 'payrollRecordId', 'payrollUpdatedAt',
  'deductions', 'advances', 'reimbursements', 'bankAccount', 'bankDetails',
  'routingNumber', 'taxWithholding',
];
const sensitive = new Set(PRIVATE_FINANCIAL_FIELDS);
const isPlainMap = value => value && Object.getPrototypeOf(value) === Object.prototype;

export function hasPrivateFinancialData(value) {
  if (Array.isArray(value)) return value.some(hasPrivateFinancialData);
  if (!isPlainMap(value)) return false;
  return Object.entries(value).some(([key, item]) => sensitive.has(key) || hasPrivateFinancialData(item));
}

export function partitionFinancialData(data = {}) {
  const operational = {}, financial = {};
  for (const [key, value] of Object.entries(data)) {
    // Move the whole enclosing value if a supported sensitive field is nested.
    (sensitive.has(key) || hasPrivateFinancialData(value) ? financial : operational)[key] = value;
  }
  return { operational, financial };
}

export function billingRatesOnly(rates = {}) {
  return Object.fromEntries(Object.entries(rates).filter(([, rate]) => rate && typeof rate === 'object')
    .map(([role, rate]) => [role, { billRate: Number(rate.billRate || 0) }]));
}

export const privateFinancialId = sourcePath => encodeURIComponent(sourcePath);

export function canReadFinancialKind(access, kind) {
  if (!access?.active || access.disabled || !access.tenantId) return false;
  const role = String(access.role || '').toLowerCase();
  if (['owner_admin', 'admin', 'finance_admin'].includes(role)) return true;
  if (['worker', 'facility_client', 'facility_admin', 'facility_admin_viewer', 'facility_viewer', 'facility_pulse'].includes(role)) return false;
  if (kind === 'rateVersion') return access.permissions?.canViewRates === true || access.permissions?.canViewPayroll === true;
  return access.permissions?.[kind === 'facilityProfiles' ? 'canViewRates' : 'canViewPayroll'] === true;
}
